<script lang="ts">
import { defineComponent, PropType, ref, reactive, computed, watch, onMounted } from 'vue'
import SearchWord from '@/components/search/SearchWord.vue'
import SearchWordWrapperForSP from '@/components/search/SearchWordWrapperForSP.vue'
import TabDefault from '@/components/tab/TabDefault.vue'
import { searchIndexPath } from '@/generated/railsRoutes'
import stateInitializer from '@/models/stateInitializer'
import { isCurrentPage } from '@/utilities/path'
import { useClientStore } from '@/stores/client'
import { useMediaQueryStore } from '@/stores/mediaQuery'

export default defineComponent({
  name: 'SearchContentsHeader',

  components: {
    SearchWord,
    SearchWordWrapperForSP,
    TabDefault,
  },

  props: {
    /**
     * 検索カテゴリ情報
     */
    categories: {
      type: Object as PropType<Record<string, { key: string, label: string }>>,
      required: true,
    },

    /**
     * クエリパラメータ用の key 情報
     */
    queryKeys: {
      type: Object as PropType<Record<'KEYWORD' | 'FILTER', string>>,
      required: true,
    },

    /**
     * 検索結果の総件数
     */
    resultTotal: {
      type: Number,
      required: true,
    },
  },

  emits: ['update:searchWord', 'update:currentTabKey'],

  setup(props, { emit }) {
    // store
    const clientStore = useClientStore()
    const mediaQueryStore = useMediaQueryStore()

    interface State {
      search: {
        input: InputText,
        result: {
          article: {
            totalCount: number,
          },
          keyword: {
            totalCount: number,
          },
          report: {
            totalCount: number,
          },
          document: {
            totalCount: number,
          },
        }
      },
    }
    const state: State = reactive({
      search: {
        input: {
          value: '',
          name: '',
        },
        result: {
          article: {
            totalCount: 0,
          },
          keyword: {
            totalCount: 0,
          },
          report: {
            totalCount: 0,
          },
          document: {
            totalCount: 0,
          },
        }
      },
    })
    stateInitializer(state, 'contentsHeader')

    const mounted = ref(false)
    const isOpenSearchWordForSP = ref(false)

    const tab = computed<TabItem[]>(() => {
      const _keywordQuery = state.search.input.value ? `${props.queryKeys.KEYWORD}=${state.search.input.value}` : ''
      let _queries: string[] = []
      let _to = ''
      return Object.values(props.categories).map(v => {
        if (v.key !== props.categories.index.key) _queries.push(`${props.queryKeys.FILTER}=${v.key}`)
        if (_keywordQuery) _queries.push(_keywordQuery)
        const queryParams = _queries.length ? `?${_queries.join('&')}` : ''
        _to = `${searchIndexPath()}${queryParams}`

        // 遷移先パスを生成後、次のタブのためにクエリパラメータ配列を初期化
        _queries = []

        const _key = v.key as keyof typeof state.search.result

        return {
          key: v.key,
          label: v.label,
          to: _to,
          isShow: _key === props.categories.index.key || (state.search.result[_key] && !!state.search.result[_key].totalCount)
        }
      })
    })

    /**
     * 現在ページに該当するタブの key を算出する
     */
    const currentTabKey = computed<string>(() => {
      const _currentTabKey = tab.value.find(v => {
        // 一致検索対象のクエリパラメータの key 名配列
        const targetQueries = []
        targetQueries.push(props.queryKeys.FILTER)

        const searchWordQueryKey = state.search.input.value ? props.queryKeys.KEYWORD : ''
        if (searchWordQueryKey) targetQueries.push(searchWordQueryKey)

        return v.to && isCurrentPage(v.to, targetQueries)
      })?.key || ''

      return _currentTabKey
    })

    const hasSearchWordForSP = computed<boolean>(() => {
      return mediaQueryStore.context.isUnderTablet
    })

    const searchWordSizeForSP = computed(() => {
      return mediaQueryStore.context.isUnderPhablet
        ? 'normal'
        : 'large'
    })

    /**
     * タブの表示条件
     * @description アプリでないこと（Web であること）、且つ検索結果が 1 件でも存在していること
     */
    const isShowTab = computed(() => !clientStore.context.isApp && props.resultTotal)

    onMounted(() => {
      // teleport 利用するための onMounted 完了フラグを立てる
      mounted.value = true

      emit('update:searchWord', state.search.input.value)
      emit('update:currentTabKey', currentTabKey.value)
    })

    const openSearchWordForSP = () => {
      if (hasSearchWordForSP.value) isOpenSearchWordForSP.value = true
    }

    return {
      // data
      state,
      tab,
      mounted,
      isOpenSearchWordForSP,

      // computed
      currentTabKey,
      hasSearchWordForSP,
      searchWordSizeForSP,
      isShowTab,

      // methods
      openSearchWordForSP,
    }
  },
})
</script>

<template>
  <section class="f-section">
    <div class="f-section--body">
      <SearchWord
        v-model="state.search.input.value"
        :name="state.search.input.name"
        size="large"
        color="white"
        max-width="520px"
        :readonly="hasSearchWordForSP"
        @click:form="openSearchWordForSP" />

      <!--
        タッチデバイス用の検索フォームは、スクリーンロックレイヤーの上に配置されるので、
        スクリーンロックレイヤーより大きい z-index としたいが、
        main 要素配下にいると、親の z-index 値にレンダリングフローが依存するため、
        vue teleport でレンダリング場所を移動させる。
        @see https://ja.vuejs.org/guide/built-ins/teleport

        ※ teleport 先が Vue インスタンスに紐づく DOM のため、
          onMounted で DOM が生成されたのを確認してからレンダリングする
      -->
      <template v-if="mounted && hasSearchWordForSP">
        <teleport to="#global-container">
          <SearchWordWrapperForSP
            v-model:is-open="isOpenSearchWordForSP"
            @close="isOpenSearchWordForSP = false">
            <SearchWord
              v-model="state.search.input.value"
              :name="state.search.input.name"
              :size="searchWordSizeForSP" />
          </SearchWordWrapperForSP>
        </teleport>
      </template>

      <TabDefault
        v-if="isShowTab"
        :items="tab"
        :current-tab-key="currentTabKey" />
    <!-- /.f-section body --></div>
  <!-- /.f-section --></section>
</template>
